import {
  Container,
  PasswordInput,
  TextInput,
  Button,
  Card,
  MantineTheme,
  useMantineTheme,
  Flex,
  LoadingOverlay,
  Stack,
  Text,
  Modal,
  useMantineColorScheme,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useState } from "react";

import LogoElsa from "../../static/svg/logo-elsa-bold.svg?react";
import { useAuth } from "../../context";
import { passwordService } from "../../api";
import { useTranslation } from "react-i18next";

export const LoginPage = () => {
  const [opened, setOpened] = useState(false);
  const [sending, setSending] = useState(false);

  const theme: MantineTheme = useMantineTheme();
  const { login, loading } = useAuth();
  const { t } = useTranslation();
  const { colorScheme } = useMantineColorScheme();

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      forgotPassword: "",
    },
    validate: {
      email: (value) =>
        /^\S+@\S+$/.test(value) ? null : t("login.emailPlaceholder"),
      password: (value) => (value ? null : t("login.passwordPlaceholder")),
      forgotPassword: (value) =>
        /^\S+@\S+$/.test(value) ? null : t("login.emailPlaceholder"),
    },
  });

  const onSubmit = async () => {
    try {
      const email = form.validateField("email");
      const password = form.validateField("password");
      if (!email.hasError && !password.hasError)
        await login(form.values.email, form.values.password);
    } catch (error) {
      console.error(error);
    }
  };

  const forgotPassword = async () => {
    try {
      const email = form.validateField("forgotPassword");
      if (!email.hasError) {
        setSending(true);
        await passwordService.forgotPassword(form.values.forgotPassword);
        showNotification({
          title: t("login.emailSent"),
          message: t("login.forgotPasswordNotificationMessage"),
          color: "blue",
        });
        setOpened(false);
        form.reset();
      }
    } catch (error: any) {
      const errorMessage: string = error.response.data.error;
      showNotification({
        title: "Error",
        message: errorMessage,
        color: "red",
      });
      setOpened(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <Container
      fluid
      px={0}
      my={0}
      h="100vh"
      style={{
        backgroundColor:
          colorScheme === "dark" ? theme.colors.dark[8] : theme.colors.red[0],
      }}
    >
      <Flex direction="column" align="center" justify="center" h="100%">
        <Container w={{ base: "80%", sm: "50%" }}>
          <Flex justify="center" align="center" h="100%">
            <LogoElsa
              style={{
                width: "60%", // Ajusta el ancho deseado
                height: "auto", // Mantén la proporción
                alignSelf: "center",
              }}
              fill={
                colorScheme === "dark"
                  ? "white"
                  : theme.colors["genderlab-red"][4]
              }
            />
          </Flex>
        </Container>
        <Container w={{ base: "80%", sm: "40%" }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            <Card
              shadow="lg"
              padding="xl"
              radius="md"
              style={{
                backgroundColor:
                  colorScheme === "dark" ? theme.colors.dark[7] : "white",
                border: `1px solid ${
                  colorScheme === "dark"
                    ? theme.colors.gray[8]
                    : theme.colors.gray[3]
                }`,
              }}
            >
              <Flex direction="column" h="100%" justify="space-between">
                <Title order={2} mb="md">
                  {t("login.title")}
                </Title>
                <LoadingOverlay visible={loading} overlayProps={{ blur: 2 }} />
                <Stack gap="lg">
                  <TextInput
                    type="email"
                    label={t("login.email")}
                    placeholder={t("login.emailPlaceholder")}
                    {...form.getInputProps("email")}
                  />
                  <PasswordInput
                    label={t("login.password")}
                    placeholder={t("login.passwordPlaceholder")}
                    {...form.getInputProps("password")}
                  />
                  <Text
                    ta="right"
                    c="dimmed"
                    size="sm"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setOpened(true);
                      form.setFieldValue("forgotPassword", form.values.email);
                    }}
                  >
                    {t("login.forgotPassword")}
                  </Text>
                </Stack>
                <Button
                  fullWidth
                  variant="filled"
                  mt="lg"
                  type="submit"
                  color={theme.colors.red[9]}
                >
                  {t("login.button")}
                </Button>
              </Flex>
            </Card>
          </form>
        </Container>
      </Flex>
      <Modal
        opened={opened}
        title={<Title order={4}>{t("login.forgotPasswordTitle")}</Title>}
        onClose={() => setOpened(false)}
        size="sm"
        styles={{
          body: {
            borderRadius: theme.radius.md,
            backgroundColor:
              colorScheme === "dark" ? theme.colors.dark[7] : "white",
          },
          title: { textAlign: "center", fontWeight: 600 },
        }}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            forgotPassword();
          }}
        >
          <Stack gap="md">
            <TextInput
              type="email"
              label="Email"
              placeholder={t("login.emailPlaceholder")}
              {...form.getInputProps("forgotPassword")}
            />
            <Button
              fullWidth
              type="submit"
              disabled={sending}
              color={theme.colors.red[9]}
            >
              {t("login.sendEmail")}
            </Button>
          </Stack>
        </form>
      </Modal>
    </Container>
  );
};
